import { ScheduleDef } from '../types';

/**
 * 行程定义表（定稿重写）
 * 训练（15 项路径训练 + 6 项特殊训练）/ 工作（10 个，五路径两档制）/ 休息（3 项）
 *
 * 解锁/升级规则：
 *   - 路径训练：同 path 三档按「路径累计次数」依次替换（12 次升中级、30 次升高级），与咖位无关；
 *     升级门槛用 `requirePathCounts` 表达（中级 { path: 12 }、高级 { path: 30 }）。
 *   - 特殊训练：无 path、无 level、不参与替换升级；`requirePathCounts`（双路径 ≥8）全满足才出现，
 *     解锁后永久保留。
 *   - 工作：五条工种路径（拍摄 shoot / 活动 event / 舞台 stage / 直播 live / 演出 performance）与五条
 *     训练路径一一对齐，每条**两档**。一档咖位 Lv.1 无训练条件即可接；二档需咖位 Lv.3 且对齐训练
 *     路径累计 ≥16，解锁后同 `trade` 内通过 `replaces` 替换一档。试镜为独立行程（无咖位门槛、不入本表）。
 *   - 体力统一口径（2026-10-08 用户令）：所有消耗体力的行程一律 25/周（试镜/通告同口径，
 *     见 GameConfig.AUDITION/CONTRACT_ENERGY_COST）——25×4=100 恰为体力上限，「排满整月=烧光体力」
 *     一句话可算；体力不足不再是排程约束（执行期兜底改在家休息），选条行内不再显示体力消耗。
 *   - 所有 `attributeGain` 一律不含 reputation（口碑只通过事件/剧本变动）。
 */
export const SCHEDULES: ScheduleDef[] = [
    // ===== 训练 · 镜头系 charm（形象管理 → 镜头造型 → 高定形象） =====
    {
        id: 'SCHEDULE_COURSE_CHARM_1',
        name: '形象管理',
        category: 'course',
        path: 'charm',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { charm: 2, stamina: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '镜头前的必修课：仪容、体态与表情，都要经得起特写。',
        description: '对着镜子调整站姿和微笑的角度，护肤和仪容的功课一样都不能少，镜头不会原谅懈怠的人。',
    },
    {
        id: 'SCHEDULE_COURSE_CHARM_2',
        name: '镜头造型',
        category: 'course',
        path: 'charm',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { charm: 3, temperament: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { charm: 12 },
        replaces: 'SCHEDULE_COURSE_CHARM_1',
        intro: '和专业造型团队合作，找到镜头里最上相的你。',
        description: '造型工作室的灯光亮起，老师逐帧回放你的测试镜头："眼神别躲，你天生就该吃这碗饭。"',
    },
    {
        id: 'SCHEDULE_COURSE_CHARM_3',
        name: '高定形象',
        category: 'course',
        path: 'charm',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { charm: 4, temperament: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { charm: 30 },
        replaces: 'SCHEDULE_COURSE_CHARM_2',
        intro: '高定礼服加身的排场，把贵气写进默认状态。',
        description: '高定试衣间的镜子前，设计师围着你转了三圈："衣服是次要的，我要的是穿上它的那股劲儿。"',
    },

    // ===== 训练 · 表演系 talent（台词正音 → 体验派表演 → 方法派研修） =====
    {
        id: 'SCHEDULE_COURSE_TALENT_1',
        name: '台词正音',
        category: 'course',
        path: 'talent',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { talent: 2, temperament: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '吐字、气息与台词功底，演员的基本功从这里打起。',
        description: '在排练厅里一遍遍打磨发音位置，老师说你演得还有点生涩，但眼神里已经有了光。',
    },
    {
        id: 'SCHEDULE_COURSE_TALENT_2',
        name: '体验派表演',
        category: 'course',
        path: 'talent',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { talent: 3, eq: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { talent: 12 },
        replaces: 'SCHEDULE_COURSE_TALENT_1',
        intro: '表演进阶课：从「演出来」走向「活成她」。',
        description: '老师带你做无实物练习，一段即兴哭戏结束，排练厅安静了几秒——你的眼眶也是红的。',
    },
    {
        id: 'SCHEDULE_COURSE_TALENT_3',
        name: '方法派研修',
        category: 'course',
        path: 'talent',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { talent: 4, eq: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { talent: 30 },
        replaces: 'SCHEDULE_COURSE_TALENT_2',
        intro: '深研角色的来路与内心，让每个反应都有出处。',
        description: '研修班的围读持续到深夜，你在角色小传的空白处写满批注，第一次离角色那么近。',
    },

    // ===== 训练 · 仪态系 temperament（礼仪仪态 → 艺术修养 → 气场美学） =====
    {
        id: 'SCHEDULE_COURSE_TEMPERAMENT_1',
        name: '礼仪仪态',
        category: 'course',
        path: 'temperament',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { temperament: 2, charm: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '从细节里磨出分寸感，站出去就是体面。',
        description: '背贴着墙站了半小时，走路、落座、递名片每个动作拆开来练，优雅原来这么累。',
    },
    {
        id: 'SCHEDULE_COURSE_TEMPERAMENT_2',
        name: '艺术修养',
        category: 'course',
        path: 'temperament',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { temperament: 3, eq: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { temperament: 12 },
        replaces: 'SCHEDULE_COURSE_TEMPERAMENT_1',
        intro: '审美是养出来的慢功夫，眼界到了气质就到。',
        description: '画廊、音乐会、老电影排满了这一周，笔记记了半本，看世界的眼神好像不太一样了。',
    },
    {
        id: 'SCHEDULE_COURSE_TEMPERAMENT_3',
        name: '气场美学',
        category: 'course',
        path: 'temperament',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { temperament: 4, charm: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { temperament: 30 },
        replaces: 'SCHEDULE_COURSE_TEMPERAMENT_2',
        intro: '练的是存在感：还没开口，气场先到。',
        description: '灯光亮起，你走到标记点站定，全场安静——不用开口，你就是画面的中心。',
    },

    // ===== 训练 · 形体系 stamina（健身塑形 → 舞蹈进修 → 极限体能） =====
    {
        id: 'SCHEDULE_COURSE_STAMINA_1',
        name: '健身塑形',
        category: 'course',
        path: 'stamina',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { stamina: 2, charm: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '把身体练成扛得住连轴转的本钱。',
        description: '力量训练加形体拉伸，汗水顺着脖颈滴落，镜中的线条一天比一天好看。',
    },
    {
        id: 'SCHEDULE_COURSE_STAMINA_2',
        name: '舞蹈进修',
        category: 'course',
        path: 'stamina',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { stamina: 3, temperament: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { stamina: 12 },
        replaces: 'SCHEDULE_COURSE_STAMINA_1',
        intro: '柔韧、控制与乐感，身体轻盈起来的秘密。',
        description: '把杆、地板、镜墙，一个八拍一个八拍地磨，离开时身体还带着律动。',
    },
    {
        id: 'SCHEDULE_COURSE_STAMINA_3',
        name: '极限体能',
        category: 'course',
        path: 'stamina',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { stamina: 4, charm: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { stamina: 30 },
        replaces: 'SCHEDULE_COURSE_STAMINA_2',
        intro: '顶着极限练出的底子，高强度行程的入场券。',
        description: '封闭训练馆里，教练掐着表让你突破最后一组极限，肺像着了火，但你撑住了。',
    },

    // ===== 训练 · 沟通系 eq（即兴表达 → 公关应答 → 危机公关） =====
    {
        id: 'SCHEDULE_COURSE_EQ_1',
        name: '即兴表达',
        category: 'course',
        path: 'eq',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { eq: 2, talent: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '张口就来的反应力，接得住任何突发状况。',
        description: '即兴课堂的题目一个比一个刁钻，你接得跌跌撞撞，笑声却越来越响。',
    },
    {
        id: 'SCHEDULE_COURSE_EQ_2',
        name: '公关应答',
        category: 'course',
        path: 'eq',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { eq: 3, temperament: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { eq: 12 },
        replaces: 'SCHEDULE_COURSE_EQ_1',
        intro: '说话的分寸与机锋，镁光灯前的护身符。',
        description: '模拟采访一轮接一轮，刁钻问题层出不穷，你学着把每个坑都变成舞台。',
    },
    {
        id: 'SCHEDULE_COURSE_EQ_3',
        name: '危机公关',
        category: 'course',
        path: 'eq',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { eq: 4, temperament: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { eq: 30 },
        replaces: 'SCHEDULE_COURSE_EQ_2',
        intro: '翻车现场怎么救？危机处理的硬功课。',
        description: '案例复盘会上，导师的复盘刀刀见血："记住，舆论场没有下不为例。"',
    },

    // ===== 训练 · 智识系 wisdom（剧本围读 → 角色心理 → 文学修养） =====
    {
        id: 'SCHEDULE_COURSE_WISDOM_1',
        name: '剧本围读',
        category: 'course',
        path: 'wisdom',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { wisdom: 2, talent: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '把剧本读薄再读厚，角色才立得住。',
        description: '围读桌上的剧本被你翻出了毛边，每一场戏的潜台词都写在页边批注里——读透剧本，是读懂角色的第一步。',
    },
    {
        id: 'SCHEDULE_COURSE_WISDOM_2',
        name: '角色心理',
        category: 'course',
        path: 'wisdom',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { wisdom: 3, eq: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { wisdom: 12 },
        replaces: 'SCHEDULE_COURSE_WISDOM_1',
        intro: '往角色心里走一趟，演谁之前先懂谁。',
        description: '你给角色写了一份"童年档案"，从原生家庭写到创伤事件——心理顾问看完说："她不是角色，是活人。"',
    },
    {
        id: 'SCHEDULE_COURSE_WISDOM_3',
        name: '文学修养',
        category: 'course',
        path: 'wisdom',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { wisdom: 4, temperament: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { wisdom: 30 },
        replaces: 'SCHEDULE_COURSE_WISDOM_2',
        intro: '眼界和谈吐的沉淀，气质里最贵的那部分。',
        description: '书页间那些没写出来的话，比写出来的更难懂。合上书的那一刻，你对人和事，好像懂了一点。',
    },

    // ===== 训练 · 才艺系 artistry（声乐入门 → 器乐研修 → 全能舞台） =====
    {
        id: 'SCHEDULE_COURSE_ARTISTRY_1',
        name: '声乐入门',
        category: 'course',
        path: 'artistry',
        level: 1,
        energyCost: 25,
        moneyCost: 2000,
        attributeGain: { artistry: 2, stamina: 1 },
        moneyGain: 0,
        energyRestore: 0,
        intro: '开口不虚的第一课，音准是地基。',
        description: '从气息到咬字一点点抠，老师弹着琴带着你爬音阶——嗓子是乐器，先学会调琴。',
    },
    {
        id: 'SCHEDULE_COURSE_ARTISTRY_2',
        name: '器乐研修',
        category: 'course',
        path: 'artistry',
        level: 2,
        energyCost: 25,
        moneyCost: 3500,
        attributeGain: { artistry: 3, temperament: 1 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { artistry: 12 },
        replaces: 'SCHEDULE_COURSE_ARTISTRY_1',
        intro: '多一门乐器，舞台上就多一种可能。',
        description: '指尖磨出了茧，一首曲子拆成八段练，琴行的老师说你手上终于有了"音乐的感觉"。',
    },
    {
        id: 'SCHEDULE_COURSE_ARTISTRY_3',
        name: '全能舞台',
        category: 'course',
        path: 'artistry',
        level: 3,
        energyCost: 25,
        moneyCost: 5000,
        attributeGain: { artistry: 4, charm: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { artistry: 30 },
        replaces: 'SCHEDULE_COURSE_ARTISTRY_2',
        intro: '唱跳演一站式打磨，为大舞台做万全准备。',
        description: '声乐、器乐、舞蹈串在一起排，一天换三套训练服——全能的意思是，哪一样都不能是短板。',
    },

    // ===== 特殊训练（2026-10-30 特训重做：3 项——主持/唱跳/武打，每项对应一枚能力 tag，是 tag 的唯一获取途径。
    // 双路径 ≥8 前置解锁，与路径训练永久共存，不参与替换升级、不计入路径累计；同一项累计 10 次授对应 TAG_SKILL_*，
    // 结算落 ScheduleSystem.settleSpecialTraining。旧六项（综艺感/广告演技/台风/访谈/风格塑造）已删——
    // 纯形象与广告向成长回归基础训练，无 tag 出口（设计见 docs/玩法设计.md「特训重做＋能力 tag」）） =====
    {
        id: 'SCHEDULE_COURSE_SPECIAL_MARTIAL',
        name: '武术特训',
        category: 'course',
        energyCost: 25,
        moneyCost: 3000,
        // 沿用旧 id（COURSE_SCENE 场景映射与 Events 事件挂载都指向它，复用无缝）；属性对不变=体能+演技
        attributeGain: { stamina: 2, talent: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { stamina: 8, talent: 8 },
        intro: '打戏亲自上阵的资本，武行功课里一点点堆出来。',
        description: '压腿、受力、走位，一个招式拆成十遍磨——武行师傅说，打戏比的不是谁拳硬，是打得好看还知道分寸。',
    },
    {
        id: 'SCHEDULE_COURSE_SPECIAL_HOST',
        name: '主持特训',
        category: 'course',
        energyCost: 25,
        moneyCost: 3000,
        attributeGain: { eq: 2, wisdom: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { eq: 8, wisdom: 8 },
        intro: '话筒前的功课：脱稿、接梗、控场。',
        description: '模拟直播现场一轮轮实战，导师冷不丁抛梗刁难——练到后来，台上出现什么状况你都能稳稳接住再递回去。',
    },
    {
        id: 'SCHEDULE_COURSE_SPECIAL_SINGDANCE',
        name: '唱跳特训',
        category: 'course',
        energyCost: 25,
        moneyCost: 3000,
        attributeGain: { artistry: 2, stamina: 2 },
        moneyGain: 0,
        energyRestore: 0,
        requirePathCounts: { artistry: 8, stamina: 8 },
        intro: '唱跳一体：气口和体力都是舞台的命。',
        description: '边跑边唱练气口，一支舞跳到能边跳边稳稳开口——老师说，唱跳舞台没有喘气的机会，练的就是这份不喘。',
    },

    // ===== 工作 · 演出路径（剧场替补 → 话剧主演） =====
    {
        id: 'SCHEDULE_WORK_THEATER',
        name: '剧场替补',
        category: 'work',
        trade: 'performance',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { talent: 1 },
        moneyGain: 2000,
        energyRestore: 0,
        fameGain: 0,
        intro: '离舞台最近的岗位，等一个登台的机会。',
        description: '你在侧幕看完一整场，台词在嘴里滚了一百遍——替补的意义就是：随时能上，但没人知道你什么时候能上。',
    },
    {
        id: 'SCHEDULE_WORK_LEADACT',
        name: '话剧主演',
        category: 'work',
        trade: 'performance',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { talent: 2 },
        moneyGain: 6000,
        energyRestore: 0,
        fameGain: 5,
        requireRank: 3,
        requirePathCounts: { talent: 16 },
        replaces: 'SCHEDULE_WORK_THEATER',
        intro: '独当一面的现场演出，真刀真枪的演技试炼。',
        description: '大幕拉开，台下一千双眼睛亮着。没有 NG，没有替身，两个小时演到底——谢幕的掌声就是剧场的加冕。',
    },

    // ===== 工作 · 拍摄路径（平面拍摄 → 杂志封面） =====
    {
        id: 'SCHEDULE_WORK_PHOTO',
        name: '平面拍摄',
        category: 'work',
        trade: 'shoot',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { charm: 1 },
        moneyGain: 2500,
        energyRestore: 0,
        fameGain: 0,
        intro: '镜头表现力的日常功课，作品就是履历。',
        description: '摄影棚里换了六套衣服，快门声连成一片，摄影师从镜头后探出头："对，就要这个感觉。"',
    },
    {
        id: 'SCHEDULE_WORK_MAGAZINE',
        name: '杂志封面',
        category: 'work',
        trade: 'shoot',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { charm: 2 },
        moneyGain: 8000,
        energyRestore: 0,
        fameGain: 5,
        requireRank: 3,
        requirePathCounts: { charm: 16 },
        replaces: 'SCHEDULE_WORK_PHOTO',
        intro: '大刊封面是时尚圈的入场券，也是咖位的注脚。',
        description: '大刊封面的拍摄现场，化妆两小时、拍摄一整天，你看着成片里那个陌生又耀眼的自己，一切都值了。',
    },

    // ===== 工作 · 活动路径（商演路演 → 品牌站台） =====
    {
        id: 'SCHEDULE_WORK_TOUR',
        name: '商演路演',
        category: 'work',
        trade: 'event',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { temperament: 1 },
        moneyGain: 3000,
        energyRestore: 0,
        fameGain: 0,
        intro: '跑场子赚曝光的辛苦活，新人攒履历的去处。',
        description: '城市之间的巡演像一场长途奔波，你在台上笑得标准，台下人潮的欢呼把疲惫冲淡了不少。',
    },
    {
        id: 'SCHEDULE_WORK_BRAND',
        name: '品牌站台',
        category: 'work',
        trade: 'event',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { temperament: 2 },
        moneyGain: 30000,
        energyRestore: 0,
        fameGain: 10,
        requireRank: 3,
        requirePathCounts: { temperament: 16 },
        replaces: 'SCHEDULE_WORK_TOUR',
        intro: '大牌活动的门面担当，一次亮相抵十次路演。',
        description: '追光灯下你缓缓走上舞台，品牌高层起底与你握手，台下镜头连成一片白光——这就是站台的分量。',
    },

    // ===== 工作 · 舞台路径（舞群伴舞 → 舞蹈首席） =====
    {
        id: 'SCHEDULE_WORK_DANCE',
        name: '舞群伴舞',
        category: 'work',
        trade: 'stage',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { stamina: 1 },
        moneyGain: 2000,
        energyRestore: 0,
        fameGain: 0,
        intro: '站在队伍后排，离聚光灯最近的一排。',
        description: '音乐一响你就是节拍的一部分，汗水甩在舞台灯光里，镜头扫过来的一瞬，你的动作一个都没散。',
    },
    {
        id: 'SCHEDULE_WORK_PRINCIPAL',
        name: '舞蹈首席',
        category: 'work',
        trade: 'stage',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { stamina: 2 },
        moneyGain: 20000,
        energyRestore: 0,
        fameGain: 10,
        requireRank: 3,
        requirePathCounts: { stamina: 16 },
        replaces: 'SCHEDULE_WORK_DANCE',
        intro: '从队尾跳到队首的位置，实力说了算。',
        description: '排练厅的灯为你亮到最晚。首席的位置要的不只是技巧——是整支舞队愿意跟着你的呼吸起舞。',
    },

    // ===== 工作 · 直播路径（直播带货 → 品牌专场直播） =====
    {
        id: 'SCHEDULE_WORK_LIVESTREAM',
        name: '直播带货',
        category: 'work',
        trade: 'live',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { eq: 1 },
        moneyGain: 5000,
        energyRestore: 0,
        fameGain: 0,
        intro: '镜头前即兴发挥的赚钱活，越熟练越值钱。',
        description: '补光灯环成一圈，你对着镜头熟练地拆解产品卖点，弹幕刷得飞快——这场下来，又是不错的一笔。',
    },
    {
        id: 'SCHEDULE_WORK_BRANDLIVE',
        name: '品牌专场直播',
        category: 'work',
        trade: 'live',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { eq: 2 },
        moneyGain: 25000,
        energyRestore: 0,
        fameGain: 10,
        requireRank: 3,
        requirePathCounts: { eq: 16 },
        replaces: 'SCHEDULE_WORK_LIVESTREAM',
        intro: '舞台级配置的直播专场，卖货与热度一起收。',
        description: '品牌为你包下整场直播，舞台级的灯光和布景，在线人数一路飙升——这一场，你就是平台的主角。',
    },

    // ===== 工作 · 音乐路径（酒吧驻唱 → 音乐节嘉宾） =====
    {
        id: 'SCHEDULE_WORK_GIG',
        name: '酒吧驻唱',
        category: 'work',
        trade: 'music',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { artistry: 1 },
        moneyGain: 2500,
        energyRestore: 0,
        fameGain: 0,
        intro: '小场子里磨唱功和胆量，音乐路的起点。',
        description: '晚上十点的酒吧灯光昏暗，你抱着吉他自弹自唱，嘈杂人声里没人认真听——每一晚，你都唱给未来的自己。',
    },
    {
        id: 'SCHEDULE_WORK_FESTIVAL',
        name: '音乐节嘉宾',
        category: 'work',
        trade: 'music',
        energyCost: 25,
        moneyCost: 0,
        attributeGain: { artistry: 2 },
        moneyGain: 15000,
        energyRestore: 0,
        fameGain: 5,
        requireRank: 3,
        requirePathCounts: { artistry: 16 },
        replaces: 'SCHEDULE_WORK_GIG',
        intro: '从酒吧到万人场，音乐路的跃级台阶。',
        description: '下午四点的音乐节舞台，台下是望不到头的年轻人。你的三首歌唱完，人潮里的合唱声比音响还响。',
    },

    // ===== 休息类（蓄能恢复体力；在家休息免费，其余消耗金钱） =====
    {
        id: 'SCHEDULE_REST_1',
        name: '在家休息',
        category: 'rest',
        energyCost: 0,
        moneyCost: 0,
        attributeGain: {},
        moneyGain: 0,
        energyRestore: 30,
        intro: '零成本的休息日，让一切慢下来。',
        description: '拉上窗帘，窝在沙发里看一部老电影，什么都不用想，好好充个电。',
    },
    {
        id: 'SCHEDULE_REST_2',
        name: 'SPA养生',
        category: 'rest',
        energyCost: 0,
        moneyCost: 2000,
        attributeGain: {},   // 2026-09-29 休息属性清零：休息只回体力，⛔ 白送气质（防「瘫着也涨属性」的暗漏）
        moneyGain: 0,
        energyRestore: 50,
        intro: '花钱买放松，紧绷的一周值得被善待。',
        description: '精油配上舒缓的音乐，理疗师的手艺让你紧绷了一周的神经彻底放松下来。',
    },
    {
        id: 'SCHEDULE_REST_3',
        name: '度假旅行',
        category: 'rest',
        energyCost: 0,
        moneyCost: 10000,
        attributeGain: {},   // 2026-09-29 休息属性清零（同 SPA）
        moneyGain: 0,
        energyRestore: 80,
        intro: '彻底断联的远行，回来满血复活。',
        description: '飞到海边感受阳光和沙滩，摆脱行程表的束缚，回来时的你元气满满、灵感迸发。',
    },
];

/** 按 ID 索引行程 */
export const SCHEDULE_MAP: Record<string, ScheduleDef> = {};
SCHEDULES.forEach(s => { SCHEDULE_MAP[s.id] = s; });